/**
 * A modal dialogue on the native <dialog> element.
 *
 * `showModal()` gives focus trapping, Escape to close, an inert background and
 * focus restoration for free, none of which a hand-rolled overlay gets right on
 * the first attempt. The caller controls it through `open`; Escape and the
 * backdrop report back through `onClose` so state stays in React.
 *
 * `dismissible` is false while a request is in flight, so a publish or upload
 * cannot be waved away half-way and leave the screen claiming nothing happened.
 */
import { useEffect, useRef, type ReactNode } from 'react';

export function Dialog({
  open,
  title,
  onClose,
  dismissible = true,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  dismissible?: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="bp-dialog"
      aria-labelledby="bp-dialog-title"
      onCancel={(e) => {
        // Escape. Always prevent the native close so React remains the owner.
        e.preventDefault();
        if (dismissible) onClose();
      }}
      onClick={(e) => {
        // A click on the backdrop lands on the <dialog> itself, not a child.
        if (dismissible && e.target === ref.current) onClose();
      }}
    >
      {open ? (
        <div className="bp-dialog-body">
          <h2 id="bp-dialog-title">{title}</h2>
          {children}
        </div>
      ) : null}
    </dialog>
  );
}
